import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { renderInvoicePdf, renderInvoicePdfBase64 } from "../lib/pdf";
import { Navbar } from "../components/Navbar";
import { InvoiceDocument } from "../components/InvoiceDocument";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download, Loader2, Printer, Mail, Check } from "lucide-react";

export default function InvoiceView() {
  const { id } = useParams();
  const invoiceRef = useRef(null);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    api
      .get(`/invoices/${id}`)
      .then(({ data }) => setInvoice(data))
      .catch(() => setError("Invoice not found"));
  }, [id]);

  const downloadPdf = async () => {
    if (!invoiceRef.current) return;
    setDownloading(true);
    try {
      const pdf = await renderInvoicePdf(invoiceRef.current);
      pdf.save(`${invoice?.invoice_number || "invoice"}.pdf`);
    } catch (err) {
      setError("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const emailPdf = async () => {
    if (!invoiceRef.current || !invoice) return;
    setEmailing(true);
    setEmailed(false);
    try {
      const pdf_base64 = await renderInvoicePdfBase64(invoiceRef.current);
      await api.post(`/invoices/${invoice.id}/email`, { pdf_base64 });
      setEmailed(true);
    } catch (err) {
      setError("Could not send email");
    } finally {
      setEmailing(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-500" data-testid="invoice-view-error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 no-print">
          <Link
            to="/contractor/invoices"
            data-testid="back-to-invoices"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to invoices
          </Link>
          <div className="flex gap-3">
            <Button
              data-testid="email-invoice-btn"
              variant="outline"
              onClick={emailPdf}
              disabled={emailing || !invoice}
              className="h-10"
            >
              {emailing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : emailed ? (
                <Check className="w-4 h-4 mr-1.5 text-emerald-500" />
              ) : (
                <Mail className="w-4 h-4 mr-1.5" />
              )}
              {emailed ? "Sent" : "Email"}
            </Button>
            <Button
              data-testid="print-btn"
              variant="outline"
              onClick={() => window.print()}
              className="h-10"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
            <Button
              data-testid="download-pdf-btn"
              onClick={downloadPdf}
              disabled={downloading || !invoice}
              className="h-10 bg-sky-500 hover:bg-sky-600"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Download PDF
            </Button>
          </div>
        </div>

        {invoice ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-slate-100 rounded-xl p-4 sm:p-8 border border-slate-200"
          >
            <InvoiceDocument ref={invoiceRef} data={invoice} />
          </motion.div>
        ) : (
          <div className="py-20 text-center text-slate-400">Loading…</div>
        )}
      </div>
    </div>
  );
}
